// Helpers for the Finnish company registry (YTJ / PRH open data).
//
// Format: seven digits, a hyphen and a checksum digit (1234567-8). The checksum
// uses the weights 7, 9, 10, 5, 8, 4, 2 modulo 11; a remainder of 1 makes the ID
// impossible, so those are always invalid. Mirrors the backend validator in
// organizations/serializers.py (validate_finnish_business_id) — the two must not
// drift, or the UI accepts IDs the API rejects on save.

const CHECKSUM_WEIGHTS = [7, 9, 10, 5, 8, 4, 2];

/**
 * Normalize user input into "1234567-8" shape as they type.
 * Digits only; the hyphen is inserted automatically after the seventh digit.
 */
export function formatBusinessId(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 7) return digits;
  return `${digits.slice(0, 7)}-${digits.slice(7)}`;
}

/** True when the value is a syntactically valid Y-tunnus with a correct checksum. */
export function isValidBusinessId(value) {
  const id = (value || '').trim();
  if (!/^\d{7}-\d$/.test(id)) return false;

  const total = CHECKSUM_WEIGHTS.reduce((sum, weight, index) => sum + Number(id[index]) * weight, 0);
  const remainder = total % 11;
  const checksum = Number(id[8]);

  if (remainder === 0) return checksum === 0;
  if (remainder === 1) return false;
  return checksum === 11 - remainder;
}

/**
 * Turn a registry city name into normal casing.
 *
 * PRH publishes post office names in capitals ("ESPOO", "MÄNTTÄ-VILPPULA"),
 * which would then shout from invoices and letters. Values that already carry
 * mixed case came from somewhere else and are left untouched.
 */
export function normalizeRegistryCity(value) {
  const city = (value || '').trim();
  if (!city || city !== city.toUpperCase()) return city;

  return city
    .toLowerCase()
    .replace(/(^|[\s\-–/])(\p{L})/gu, (match, boundary, letter) => boundary + letter.toUpperCase());
}
